// =============================================================================
// MEMORY PHOTO DISPLAY
// =============================================================================
// Large photo display with optional metadata overlay

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemoryPhoto } from '@chlann/shared';
import { withAuthToken } from '../../api/client.js';

interface MemoryPhotoDisplayProps {
  photo: MemoryPhoto;
  fit: 'contain' | 'cover';
  showInfo: boolean;
  largeText: boolean;
}

export function MemoryPhotoDisplay({
  photo,
  fit,
  showInfo,
  largeText,
}: MemoryPhotoDisplayProps) {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const displayDate = formatDate(photo.takenAt);
  const hasPeople = photo.people.length > 0;
  const hasLocation = !!photo.location;
  const hasAnyInfo = displayDate || hasPeople || hasLocation || photo.caption;

  return (
    <div className="relative h-full w-full bg-ink-1">
      {/* Loading placeholder */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-linen-300">
          <span className="mb-2 text-4xl">📷</span>
          <p>{t('memory.photoLoadError', 'Could not load photo')}</p>
        </div>
      )}

      {/* Photo */}
      <img
        src={withAuthToken(photo.fullUrl)}
        alt={photo.caption || t('memory.photo', 'Photo')}
        className={`h-full w-full transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          objectFit: fit,
        }}
        onLoad={() => { setIsLoaded(true); }}
        onError={() => { setHasError(true); }}
      />

      {/* Info overlay - bottom of photo */}
      {showInfo && hasAnyInfo && isLoaded && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-6 pb-6 pt-12">
          {/* Caption */}
          {photo.caption && (
            <p
              className={`mb-2 font-medium text-white ${
                largeText ? 'text-2xl' : 'text-lg'
              }`}
            >
              {photo.caption}
            </p>
          )}

          {/* People in photo */}
          {hasPeople && (
            <p
              className={`mb-1 text-hearth-300 ${
                largeText ? 'text-xl' : 'text-base'
              }`}
            >
              👤 {photo.people.join(', ')}
            </p>
          )}

          {/* Date and location row */}
          <div className={`flex items-center gap-4 text-linen-200 ${
            largeText ? 'text-lg' : 'text-sm'
          }`}>
            {displayDate && (
              <span>📅 {displayDate}</span>
            )}
            {hasLocation && (
              <span>📍 {photo.location}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
